import {
  geoToH3,
  kRing,
  h3GetResolution,
  h3SetToMultiPolygon,
  h3ToGeoBoundary,
  h3ToParent,
  h3ToChildren,
  polyfill,
  compact,
} from "h3-js";
import LZString from "lz-string";
import { chunk } from "lodash";
import { GeofencesLayerTypes, GeofencesSpeedLayers, GeofencesSpeedRange } from "../constants";
import { StringUtils } from "../util";
import API from "../API";

const reverseCoordinates = ([lon, lat]) => [+lat, +lon];

const guidS4 = () =>
  Math.floor((1 + Math.random()) * 0x10000)
    .toString(16)
    .substring(1);

export const guid = () => `${guidS4()}${guidS4()}${guidS4()}`;

export const getPolygonColor = (theme, layerType) =>
  theme.palette.geofencesTypeColors[layerType] || theme.palette.text.lightgrey;

export const createZone = ({
  id = null,
  ruleId = null,
  rules = [],
  name = "",
  polygon = [],
  hexes = {},
  hidden = false,
  active = true,
  persisted = false,
  created = new Date().toISOString(),
} = {}) => ({
  key: guid(),
  id,
  ruleId,
  rules,
  name,
  polygon,
  hexes,
  hidden,
  active,
  persisted,
  created,
});

export const createLayer = ({
  type = GeofencesLayerTypes.none,
  name = "",
  zones = {},
  hidden = false,
  speed = null,
  incentive = {},
} = {}) => ({
  key: guid(),
  type,
  name,
  zones,
  hidden,
  speed,
  incentive,
});

export const hexFromH3Index = (h3Index) => ({
  h3Index,
  polygon: h3ToGeoBoundary(h3Index),
});

export const getPolygonFromH3Indexes = (h3Indexes) => {
  const minResolution = Math.max(...h3Indexes.map((h3Index) => h3GetResolution(h3Index)));

  const resIndexes = h3Indexes.reduce((res, h3Index) => {
    const resolution = h3GetResolution(h3Index);

    if (resolution < minResolution) {
      h3ToChildren(h3Index, minResolution).forEach((child) => res.push(child));
    } else if (resolution === minResolution) {
      res.push(h3Index);
    }
    return res;
  }, []);

  // Each linear ring should be closed but h3 returns not closed ones
  const multipolygon = (h3SetToMultiPolygon(resIndexes) || []).map((polygon) =>
    polygon.map((ring) => [...ring, ring[0]]),
  );

  if (!multipolygon.length) return null;

  return multipolygon.length === 1 ? multipolygon[0] : multipolygon;
};

// Geometry may contain two types of coordinates:
// - A single polygon.
// - An array of polygons (multipolygon).
//
// A polygon is an array of rings (or loops, e.g. a polygon can have a hole).
// A ring is an array of (lon, lat) coordinates.
export const polygonFromGeoJSONGeometry = ({ type, coordinates }) => {
  const isPolygon = type === "Polygon";
  const isMultiPolygon = type === "MultiPolygon";
  const isPoint = type === "Point";

  if (isPolygon) {
    return coordinates.map((ring) => ring.map(reverseCoordinates));
  }

  if (isMultiPolygon) {
    return coordinates.map((polygon) => polygon.map((ring) => ring.map(reverseCoordinates)));
  }

  if (isPoint) {
    return [[reverseCoordinates(coordinates)]];
  }

  return null;
};

// Processes Layer data and uploads Rules and Geofences
export const bulkUploadLayer = async ({
  marketUrl,
  fleetUrl,
  speedLimit,
  isOperationalZone,
  noParking,
  allowParking,
  limitedParking,
  isWarehouse,
  isSidewalk,
  isActive,
  zones,
} = {}) => {
  if (!zones.length) return { geofences: [], rules: [] };

  const rulePayload = {
    active: isActive,
    market: marketUrl,
    vehicle_type: "vehicle_api.scooter",
    speed_limit: speedLimit,
    no_parking: noParking,
    allow_parking: allowParking,
    limited_parking: limitedParking,
    is_warehouse: isWarehouse,
    is_sidewalk: isSidewalk,
  };

  const geofencesPayload = zones.map((zone) => {
    const payload = {
      name: zone.name,
      fleet: fleetUrl,
      h3_indexes: Object.keys(zone.hexes),
      is_operational_zone: isOperationalZone,
    };

    if (zone.polygon) {
      payload.polygon = geoJSONGeometryFromPolygon(zone.polygon);
    }

    return payload;
  });

  let createdGeofences = [];
  let createdRules = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const geofencesChunk of chunk(
    geofencesPayload,
    window.REACT_APP_MAX_REQUEST_GEOFENCES || 50,
  )) {
    // eslint-disable-next-line no-await-in-loop
    const { geofences, rules } = await API.geofences.createBulkGeofences({
      rule: rulePayload,
      geofences: geofencesChunk,
    });
    createdGeofences = [...createdGeofences, ...geofences];
    createdRules = [...createdRules, ...rules];
  }

  return { geofences: createdGeofences, rules: createdRules };
};

export const polygonFromLeafletElement = (leafletElement) => {
  const { geometry } = leafletElement.toGeoJSON();

  return polygonFromGeoJSONGeometry(geometry);
};

export const geoJSONGeometryFromPolygon = (polygon) => {
  // The internal presentation of a polygon can be a point, a polygon or a multipolygon.
  const isMPolygon = isMultiPolygon(polygon);
  const isPoint = isPointPolygon(polygon);
  const geometry = {};

  if (isPoint) {
    geometry.coordinates = reverseCoordinates(polygon[0][0]);
    geometry.type = "Point";
  } else if (isMPolygon) {
    geometry.coordinates = polygon.map((coords) =>
      coords.map((ring) => ring.map(reverseCoordinates)),
    );
    geometry.type = "MultiPolygon";
  } else {
    geometry.coordinates = polygon.map((ring) => ring.map(reverseCoordinates));
    geometry.type = "Polygon";
  }

  return {
    type: "Feature",
    properties: {},
    geometry,
  };
};

export const zoneFromAPIRecord = (record) => {
  // Assuming we always have zero or one rule.
  // A zone is active by default.
  const { url: ruleUrl, active = true } = record.rules[0] || {};
  const { id, name } = record;
  const ruleId = ruleUrl ? StringUtils.uuidFromUrl(ruleUrl) : null;
  const rules = record.rules;
  const polygon = polygonFromGeoJSONGeometry(record.polygon?.geometry || {});
  const created = record.created;

  const hexes = record.h3_indexes.reduce((acc, h3Index) => {
    acc[h3Index] = hexFromH3Index(h3Index);

    return acc;
  }, {});

  return createZone({
    id,
    ruleId,
    rules,
    name,
    polygon,
    hexes,
    active,
    persisted: true,
    created,
  });
};

export const layerTypeFromAPIRecord = (record) => {
  const defaultLayerType = GeofencesLayerTypes.none;

  if (record.is_operational_zone) {
    return GeofencesLayerTypes.operationalZone;
  }

  if (record.rules && record.rules.length > 0 && record.rules[0].parking_incentive) {
    return GeofencesLayerTypes.parkingIncentivizedZone;
  }

  // If there are no rules the type is None.
  // A label is supposed to be equal to one of the layer types.
  const layerType =
    record.rules && record.rules.length > 0
      ? record.rules[0].label || defaultLayerType
      : defaultLayerType;

  if (layerType === "sidewalk") {
    return record.rules && record.rules.length > 0 && record.rules[0].speed_limit
      ? GeofencesLayerTypes.slowRidingZone
      : GeofencesLayerTypes.noRidingZone;
  }

  const isValidLayerType = Object.values(GeofencesLayerTypes).includes(layerType);

  return isValidLayerType ? layerType : defaultLayerType;
};

export const layerSpeedFromAPIRecord = (record) => {
  const layerType = layerTypeFromAPIRecord(record);
  const layerSpeed = (record.rules[0] || {}).speed_limit;

  if (!layerSpeed && GeofencesSpeedLayers.includes(layerType)) {
    return GeofencesSpeedRange.default;
  }
  return layerSpeed;
};

export const parkingIncentiveFromAPIRecord = (record) => {
  const parkingIncentive = record?.rules?.[0]?.parking_incentive;
  if (parkingIncentive?.amount) parkingIncentive.amount = parkingIncentive.amount.toString();
  return parkingIncentive || {};
};

export const zoneFlagsFromLayerType = (type) => {
  const flags = {
    isOperationalZone: false,
    noParking: false,
    allowParking: false,
    limitedParking: false,
    isWarehouse: false,
  };

  switch (type) {
    case GeofencesLayerTypes.operationalZone:
      flags.isOperationalZone = true;
      break;
    case GeofencesLayerTypes.noParkingZone:
      flags.noParking = true;
      break;
    case GeofencesLayerTypes.parkingAllowedZone:
      flags.allowParking = true;
      break;
    case GeofencesLayerTypes.parkingLimitedZone:
      flags.noParking = true;
      flags.limitedParking = true;
      break;
    case GeofencesLayerTypes.noRidingZone:
      flags.speedLimit = 0;
      break;
    case GeofencesLayerTypes.noScooterZone:
      flags.noParking = true;
      flags.speedLimit = 0;
      break;
    case GeofencesLayerTypes.noParkingSlowZone:
      flags.noParking = true;
      break;
    case GeofencesLayerTypes.parkingIncentivizedZone:
      flags.allowParking = true;
      break;
    case GeofencesLayerTypes.warehouse:
      flags.allowParking = true;
      flags.isWarehouse = true;
      break;
    default:
      break;
  }

  return flags;
};

export const parseGeoJSONFile = (geojson) => {
  const data = JSON.parse(geojson);

  const zones = data.features
    .map(({ geometry }) => polygonFromGeoJSONGeometry(geometry))
    .filter(Boolean)
    .reduce((res, polygon) => {
      const zone = createZone({ polygon });

      return {
        ...res,
        [zone.key]: zone,
      };
    }, {});

  const layer = createLayer({ zones });

  return { [layer.key]: layer };
};

export const parseKMLFile = (kml) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(kml, "text/xml");

  const getLayerZones = (layer, name) => {
    const elements = layer.querySelectorAll("Polygon, Point");

    const resZones = {};

    for (let index = 0; index < elements.length; index++) {
      const element = elements[index];

      const nameElement = element.parentNode.querySelector("name");
      const nameValue = nameElement ? nameElement.textContent || "" : "";
      const coordinates = [];

      if (element.nodeName === "Polygon") {
        const ringCoordinates = element.querySelectorAll("LinearRing > coordinates");

        for (let rindex = 0; rindex < ringCoordinates.length; rindex++) {
          const ring = ringCoordinates[rindex];
          const coordinatesText = ring ? ring.textContent.trim() : "";
          const parsedCoordinates = coordinatesText
            .split(/ |\n/)
            .filter(Boolean)
            .map((point) => {
              const [lat, lon] = point.split(",").slice(0, 2).reverse();
              return [+lat, +lon];
            });

          coordinates.push(parsedCoordinates);
        }
      } else {
        const coordinatesElement = element.querySelector("coordinates");
        const coordinatesText = coordinatesElement ? coordinatesElement.textContent.trim() : "";
        const [lat, lon] = coordinatesText.split(",").slice(0, 2).reverse();

        coordinates.push([[+lat, +lon]]);
      }

      const zone = createZone({
        polygon: coordinates,
        name: nameValue || (name ? `${name} — Zone ${index + 1}` : ""),
      });

      resZones[zone.key] = zone;
    }

    return resZones;
  };

  const layers = Array.from(xmlDoc.querySelectorAll("Folder")).reduce((res, folder) => {
    const nameElement = folder.querySelector("name");
    const name = nameElement ? nameElement.textContent || "" : "";

    const zones = getLayerZones(folder, name);
    const layer = createLayer({ name, zones });

    return { ...res, [layer.key]: layer };
  }, {});

  if (!Object.keys(layers).length) {
    const zones = getLayerZones(xmlDoc);

    if (Object.keys(zones).length) {
      const layer = createLayer({ zones });

      layers[layer.key] = layer;
    }
  }

  return layers;
};

export const parseAPIZonesList = (records) => {
  const layerKeyByType = {};

  return records.reduce((layers, record) => {
    const zone = zoneFromAPIRecord(record);
    const layerType = layerTypeFromAPIRecord(record);
    const layerSpeed = layerSpeedFromAPIRecord(record);
    const incentive = parkingIncentiveFromAPIRecord(record);

    const incentiveAmount = incentive?.amount || "";
    const layerKey = layerKeyByType[`${layerType}-${layerSpeed}-${incentiveAmount}`];

    if (layerKey) {
      layers[layerKey].zones[zone.key] = zone;
    } else {
      const layer = createLayer({
        type: layerType,
        zones: { [zone.key]: zone },
        speed: layerSpeed,
        incentive,
      });

      layerKeyByType[`${layerType}-${layerSpeed}-${incentiveAmount}`] = layer.key;
      layers[layer.key] = layer;
    }

    return layers;
  }, {});
};

export const isMultiPolygon = (coordinates) => typeof coordinates?.[0]?.[0]?.[0]?.[0] === "number";

export const isPolygon = (coordinates) => typeof coordinates?.[0]?.[0]?.[0] === "number";

export const isPointPolygon = (coordinates) =>
  isPolygon(coordinates) && coordinates[0].length === 1;

export const getKRing = ({ resolution, polygon }) => {
  if (!isPointPolygon(polygon)) return {};

  const [lat, lon] = polygon[0][0];
  const h3Index = geoToH3(lat, lon, resolution);

  return kRing(h3Index, 1).reduce((hexes, index) => {
    hexes[index] = hexFromH3Index(index);

    return hexes;
  }, {});
};

// Accepts polygon or multipolygon.
//
// polyfill requires input polygon in (lon, lat) format if 3rd parameter is true,
// or (lat, lon) otherwise. We store coordinates in (lat, lon).
// It also accepts polygons with multiple rings (loops).
//
// See https://h3geo.org/docs/api/regions/#polyfill.
export const fillWithHexes = ({ resolution, polygon }) => {
  const features = isPolygon(polygon) ? [polygon] : polygon;

  return features.reduce((hexes, feature) => {
    const h3Indices = polyfill(feature, resolution, false);

    h3Indices.forEach((h3Index) => {
      hexes[h3Index] = hexFromH3Index(h3Index);
    });

    return hexes;
  }, {});
};

// Replaces hexes with their parents as much as possible.
//
// See https://h3geo.org/docs/api/hierarchy#compact.
export const compactHexes = (hexes) => {
  const h3Indices = Object.keys(hexes);
  const newH3Indices = compact(h3Indices);

  return newH3Indices.reduce((newHexes, h3Index) => {
    newHexes[h3Index] = hexFromH3Index(h3Index);

    return newHexes;
  }, {});
};

// This method takes parents and see if they should be included
// based on the number of children inside the polygon (threshold).
// The output resolution remains the same.
export const fillWithParentHexes = ({ resolution, childrenThreshold, hexes }) => {
  const newHexes = {};
  const parentResolution = resolution - 1;

  const childrenCounts = Object.keys(hexes).reduce((counts, h3Index) => {
    const parentH3Index = h3ToParent(h3Index, parentResolution);

    counts[parentH3Index] = (counts[parentH3Index] || 0) + 1;

    return counts;
  }, {});

  Object.keys(childrenCounts)
    .filter((h3Index) => childrenCounts[h3Index] >= childrenThreshold)
    .forEach((h3Index) => {
      newHexes[h3Index] = newHexes[h3Index] || hexFromH3Index(h3Index);
    });

  return newHexes;
};

// Detect if there is an existing hex in the given latlng point.
// Returns h3Index and `exists` flag.
export const hasHexAt = ({ latlng, resolution, hexes }) => {
  const { lat, lng } = latlng;
  const h3Index = geoToH3(lat, lng, resolution);

  return { h3Index, exists: h3Index in hexes };
};

// In case of adding a new hex, if there are 7 hexes of child resolution
// we replace them with a parent hex.
// Or, in case of deletion of a child, we destructure a parent into 7 hexes
// and delete one of them.
// If the force param is set (true/false) then enforce toggle
export const toggleHexAt = ({ latlng, resolution, hexes, force }) => {
  const { h3Index, exists } = hasHexAt({ latlng, resolution, hexes });
  const newHexes = { ...hexes };

  if ((exists && force) || (!exists && force === false)) return newHexes;

  // Either delete a hex if it exists or add it.
  if (exists) {
    delete newHexes[h3Index];
  } else {
    const parentResolution = resolution - 1;
    const h3IndexParent = h3ToParent(h3Index, parentResolution);
    const childHexes = h3ToChildren(h3IndexParent, resolution);

    // If we click on a parent, we need to destructure it into its children
    // and delete one of them.
    //
    // Otherwise we just add a new hex and see if we should replace 7 children
    // by their parent hex.
    if (h3IndexParent in hexes) {
      delete newHexes[h3IndexParent];

      childHexes
        .filter((h3Child) => h3Child !== h3Index)
        .forEach((h3Child) => {
          newHexes[h3Child] = newHexes[h3Child] || hexFromH3Index(h3Child);
        });
    } else {
      newHexes[h3Index] = newHexes[h3Index] || hexFromH3Index(h3Index);

      const allChildHexesInside = childHexes.reduce(
        (res, h3Child) => res && Object.keys(newHexes).includes(h3Child),
        true,
      );

      if (allChildHexesInside) {
        childHexes.forEach((h3Child) => delete newHexes[h3Child]);

        newHexes[h3IndexParent] = newHexes[h3IndexParent] || hexFromH3Index(h3IndexParent);
      }
    }
  }

  return newHexes;
};

export const getGeofencesSavedData = ({ key, fleetId } = {}) => {
  const data = LZString.decompress(localStorage.getItem("geofences-editor-data"));
  const parsedData = data ? JSON.parse(data) : {};

  return key && parsedData[key]
    ? parsedData[key]
    : Object.values(parsedData)
        .sort((d1, d2) => d2.timestamp - d1.timestamp)
        .filter((d) => !fleetId || d.fleetId === fleetId)
        .reduce((res, d) => ({ ...res, [d.key]: d }), {});
};

export const setGeofencesSavedData = ({ key, fleetId, name, layers, sidewalks }) => {
  const data = getGeofencesSavedData();
  const keys = Object.keys(data);

  if (keys.length >= 5 && !data[key]) {
    delete data[keys[keys.length - 1]];
  }

  data[key] = {
    key,
    fleetId,
    name,
    layers,
    sidewalks,
    timestamp: new Date().getTime(),
  };

  localStorage.setItem("geofences-editor-data", LZString.compress(JSON.stringify(data)));
};

export const removeGeofencesSavedData = (key) => {
  const data = getGeofencesSavedData();

  delete data[key];

  localStorage.setItem("geofences-editor-data", JSON.stringify(data));
};
